import { useEffect, useState, useMemo, memo } from "react";
import { getDiscoveredCameras } from "../../../api/DeviceServiceManager";
import {
  fetchedUnPairedCameraDevices,
  setSelectedCameraQR,
  modalOpen,
  resetUnpairedCamerasRefresh,
  clearOnboardingFields,
  showNotificationBanner,
  resetTableFilterState,
  updateTableFilterState,
} from "../../../store/actions";
import { DEVICE_CONSTANTS } from "../../../constants/DeviceConstants";
import CustomHOCTable from "../../../global/components/Tables/CustomHOCTable";
import {
  getUnpairedCameraDataSource,
  getFilterOptions,
  getFilteredData,
} from "../../../global/utility/deviceTableUtils";
import { Body, Title } from "@vds/typography";
import { PAGE_IDENTIFIERS } from "../../../global/constants/tableConstants";
import NotificationBanner from "../../../layouts/NotificationBanner";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSortingIcon } from "../../../global/utility/tableUtils";
import { Button, TextLink } from "@vds/buttons";
import { Tooltip } from "antd";
import DiscoverCamera from "../../../features/camerOnboarding/discoveredCameraOnboarding";
import { hasPermission } from "../../../global/utility/permissionUtils";
import { DropdownSelect } from "@vds/selects";
import { snapshotImage, StreamType } from "../../../hoc/snapshotImage";
import { TableSnapshot } from "../../../global/components/snapshots/TableSnapshot";
import ErrorTemplate from "../../../global/components/errorTemplate/ErrorTemplate";
import { Box } from "@mui/material";
import { TEXT } from "../../../global/constants/errorTemplateConstants";

const SnapshotCell = memo(({ streams, cameraId, discoveredby }) => {
  const SnapshotImg = useMemo(
    () => snapshotImage(TableSnapshot, streams, cameraId, StreamType.UNPAIRED_CAMERA, discoveredby),
    [streams, cameraId, discoveredby]
  );
  return (
    <SnapshotImg
      cameraName={`Camera_${cameraId}`}
      streamName={`Stream_${streams}`}
    />
  );
}, (prevProps, nextProps) =>
  prevProps.streams === nextProps.streams &&
  prevProps.cameraId === nextProps.cameraId &&
  prevProps.discoveredby === nextProps.discoveredby
);

const NewUnpairedCameraTab = (props) => {
  const [unpairedCameraList, setUnpairedCameraList] = useState([]);
  const companionDeviceList = props.unpairedCameraData.companionDeviceList || [];
  const dispatch = useDispatch();
  const [unpairedCameraDataSource, setUnpairedCameraDataSource] = useState([]);
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const [showNotification, setShowNotification] = useState(false);
    const [showOfflineCCDsNotification, setShowOfflineCCDsNotification] = useState(false);
    const [showOfflineCCDsNotificationMessage, setShowOfflineCCDsNotificationMessage] = useState("");
  const [siteList, setSiteList] = useState([]);
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const { discoveredCamerasList } = useSelector((state) => state?.deviceReducer);
  const persistedSiteId = useSelector((state) => state?.tableFilterReducer?.DiscoveredCamerasTab?.selectedSiteId);
  const [selectedSiteId, setSelectedSiteId] = useState(
    persistedSiteId || siteTagList?.sites?.[0]?.id || ""
  );
  const [discoveredCameraButton, setDiscoveredCameraButton] = useState(false);
  const { refreshUnpairedList } = useSelector((state) => state.cameraonboardingReducer);
  const [showCameraOnboardingModal, setShowCameraOnboardingModal] = useState(false);
  const [noCameraTitle, setNoCameraTitle] = useState("");
  const handleUnpairedCameraAPICall = (forcediscovery) => {
    const baseCCDSiteFilter = (item) => item?.siteids?.[0] === selectedSiteId;
    let filteredCompanionDeviceList = [];
    filteredCompanionDeviceList = companionDeviceList?.filter(
        (item) => baseCCDSiteFilter(item) && item?.fields?.connectivityStatus?.toLowerCase() === DEVICE_CONSTANTS.CCD_ONLINE_STATUS
      );
    let onlineFilteredCompanionDeviceList = companionDeviceList?.filter(baseCCDSiteFilter);
    if (filteredCompanionDeviceList && filteredCompanionDeviceList?.length > 0) {
      setLoading(true);
      setError(false);
      if (onlineFilteredCompanionDeviceList.length !== filteredCompanionDeviceList.length) {
        let offlineCCDs = onlineFilteredCompanionDeviceList.filter(m => m?.fields?.connectivityStatus?.toLowerCase() !== DEVICE_CONSTANTS.CCD_ONLINE_STATUS);
        let ccdnames = offlineCCDs.map((ccd) => ccd.name).join(", ");
      setShowOfflineCCDsNotification(true);
      setShowOfflineCCDsNotificationMessage(DEVICE_CONSTANTS.CCDs_OFFLINE_BANNER_TITLE(ccdnames, offlineCCDs.length));
      }else{
        setShowOfflineCCDsNotificationMessage("");
      }
      getDiscoveredCameras({
        companionDeviceList: filteredCompanionDeviceList,
        activeSession: activeSession,
        forcediscovery: forcediscovery,
      }).then((discoveredCameras) => {
        if (discoveredCameras !== null && discoveredCameras !== undefined) {
          dispatch(fetchedUnPairedCameraDevices(discoveredCameras));
          if (discoveredCameras?.length === 0) {
            setNoCameraTitle(DEVICE_CONSTANTS.NO_DISCOVERED_CAMERAS);
          }
        } else {
          console.error("Response is null or undefined");
        }
        setDiscoveredCameraButton(false);
        setLoading(false);
        setUnpairedCameraList(discoveredCameras);
      }).catch(err => {
        console.error(err);
        setLoading(false);
        setError(true);
        setDiscoveredCameraButton(false);
      });
    } else {
      if (onlineFilteredCompanionDeviceList.length > 0) {
      setNoCameraTitle(DEVICE_CONSTANTS.ALL_CCDS_OFFLINE);
      setShowOfflineCCDsNotification(true);
      setShowOfflineCCDsNotificationMessage(DEVICE_CONSTANTS.ALL_CCDS_OFFLINE);
    } else {
      const siteName = siteTagList?.sites?.find((s) => s.id === selectedSiteId)?.name;
      setNoCameraTitle(DEVICE_CONSTANTS.NO_CCD_ASSOCIATED(siteName || selectedSiteId));
    }
      setUnpairedCameraList([]);
      setUnpairedCameraDataSource([]);
      setDiscoveredCameraButton(false);
      setLoading(false);
      setError(false);
    }
  };

  useEffect(() => {
      const notificationInfo = {
            type: "CCD Offline",
            priority: "critical",
            subtitle: showOfflineCCDsNotificationMessage,
            isDismissible: true,
            hideCloseButton: true,
            showNotification: showOfflineCCDsNotification,
          };
          dispatch(showNotificationBanner(notificationInfo));

    }, [showOfflineCCDsNotification]);
  useEffect(() => {
    if (refreshUnpairedList) {
      handleUnpairedCameraAPICall(false);
      dispatch(resetUnpairedCamerasRefresh());
    }
  }, [refreshUnpairedList]);

  useEffect(() => {
    handleUnpairedCameraAPICall(false);
  }, [selectedSiteId]);

  const handleSnapshot = async () => {
    setDiscoveredCameraButton(true);
    handleUnpairedCameraAPICall(true);
  };

  const handleCameraOnboarding = (camName, compName, streamId = null) => {
    if (!hasPermission("Cameras", "Create")) {
      setShowNotification(true);
    } else {
      if (camName) {
        setShowCameraOnboardingModal(true);
        dispatch(setSelectedCameraQR(camName));
        dispatch(
          modalOpen({
            camName,
            compName,
            streamId,
            isPairing: true,
            onBoradType: "Auto",
            editFlow: false,
          })
        );
      }
    }
  };

  const columns = useMemo(() => [
    {
      title: (
        <div style={{ display: "flex" }}>
          <Title size="small" bold primitive="h2" viewport="desktop">
            Snapshot
          </Title>
        </div>
      ),
      dataIndex: "Snapshot",
      key: "Snapshot",
      width: 200,
      render: (fov, record) => (
        <SnapshotCell
          streams={record?.Streams}
          cameraId={record?.CameraId}
          discoveredby={record?.discoveredby}
        />
      ),
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Camera ID
        </Title>
      ),
      dataIndex: "CameraId",
      key: "CameraId",
      width: 350,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => {
        const cameraA = a.CameraId || "";
        const cameraB = b.CameraId || "";
        return cameraA.localeCompare(cameraB);
      },
      sortIcon: ({ sortOrder }) => getSortingIcon(sortOrder),
      render: (value, record) => {
        if (value !== undefined) {
          return (
            <div style={{ display: "flex" }}>
              <Tooltip placement="bottomLeft" title={`camera_${value}`}>
                <TextLink
                  type="standAlone"
                  size="large"
                  className="link"
                  style={{
                    width: "100%",
                    display: "block",
                    overflow: "hidden",
                    cursor: "pointer",
                    borderBottomStyle: "none",
                    textDecoration: "underline",
                  }}
                  data-testid="discoveredCameras_cameraIdTextlink"
                  onClick={() =>
                    handleCameraOnboarding(
                      record.CameraId,
                      record.resourceidentifier,
                      record.Streams
                    )
                  }
                >
                  <Body size="large" viewport="desktop" primitive="span">
                    Camera_{value}
                  </Body>
                </TextLink>
              </Tooltip>
            </div>
          );
        }
      },
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          Streams
        </Title>
      ),
      dataIndex: "Streams",
      key: "Streams",
      width: 350,
      render: (text, record) => {
        return (
          <div
            style={{
              cursor: "pointer",
            }}
          >
            <Tooltip
              placement="bottomLeft"
              autoAdjustOverflow="false"
              title={text}
            >
              {record.Streams && record.Streams.length > 0
                ? `Stream_${record.Streams}`
                : "N/A"}
            </Tooltip>
          </div>
        );
      },
    },
      {
      dataIndex: "Companions",
      key: "Companions",
      filter: getFilterOptions(
        unpairedCameraDataSource,
        "Companions",
        "newUnpairedCameraTab"
      ),
      isFilter: true,
      onFilter: (value, record) => {
        return getFilteredData(value, record, "Companions");
      },
      width: 1,
      onHeaderCell: () => ({
        style: {
          padding: 0,
          border: "none",
        },
      }),
      render: () => null,
    },
  ], [unpairedCameraDataSource]);

  const getUnpairedCameraData = () => {
    let unpairedCameraData = getUnpairedCameraDataSource(
      unpairedCameraList,
      companionDeviceList
    );
    setUnpairedCameraDataSource(unpairedCameraData);
  };

  useEffect(() => {
    getUnpairedCameraData();
  }, [unpairedCameraList]);

  useEffect(() => {
    setSiteList(siteTagList?.sites);
  }, []);

  let filteredSites = siteList || [];

  const handleSiteToggle = (newSiteId) => {
    dispatch(resetTableFilterState({ pageIdentifier: PAGE_IDENTIFIERS.DISCOVERED_CAMERAS_TAB, preserveCurrentPage: false }));
    dispatch(updateTableFilterState({ pageIdentifier: PAGE_IDENTIFIERS.DISCOVERED_CAMERAS_TAB, propName: "selectedSiteId", value: newSiteId }));
    setUnpairedCameraDataSource([]);
    setUnpairedCameraList([]);
    setSelectedSiteId(newSiteId);
  };

  const customNoDataText = {
    emptyText: (
      <div style={{ color: "#000000" }}>
        <center>
          <br></br>
          {!unpairedCameraDataSource?.length > 0 && (
            <>
              <div style={{ marginBottom: "8px" }}>
                <Title size="large" primitive="h3">
                  {noCameraTitle}
                </Title>
              </div>
            </>
          )}

          {unpairedCameraDataSource?.length > 0 && (
            <>
              <div style={{ marginBottom: "8px" }}>
                <Title size="large" primitive="h3">
                  No discovered cameras found
                </Title>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <Body size="large" viewport="desktop">
                  Try adjusting your search.
                </Body>
              </div>
            </>
          )}
        </center>
      </div>
    ),
  };

  const UnpairedCameraTable = useMemo(() => CustomHOCTable(), []);

  // Memoize the rendered table element so it only re-creates when
  // `unpairedCameraDataSource` or `columns` change. This avoids
  // re-executing the table render block when the data hasn't changed.
  const UnpairedCameraTableElement = useMemo(() => {
    return (
      customNoDataText && <UnpairedCameraTable
        showSearch={false}
        columns={columns}
        dataList={unpairedCameraDataSource}
        rowKey="id"
        addDetailsButton={false}
        pairDeviceButton={true}
        pairDeviceButtonStyle={{ position: "relative", right: "50px" }}
        locale={customNoDataText}
        loading={loading}
        pageIdentifier={PAGE_IDENTIFIERS.DISCOVERED_CAMERAS_TAB}
        useReduxPersistence={true}
        extraAction={
          <div>
            <Button
              use="secondary"
              size="small"
              State="Default"
              width="auto"
              disabled={discoveredCameraButton}
              data-testid="devices_discoverCamerasButton"
              onClick={() => handleSnapshot()}
            >
              Discover cameras
            </Button>
          </div>
        }
      />
    );
  }, [unpairedCameraDataSource, columns, loading, customNoDataText, discoveredCameraButton, handleSnapshot]);

  // Commenting button until NPDIOTS-7768 blocker fixes.
  // const handleManualAdd = () => {
  //   dispatch(
  //     modalOpen({
  //       isPairing: true,
  //       onBoradType: "Manual",
  //     })
  //   );
  // };

  if (!loading && error && (!discoveredCamerasList || discoveredCamerasList.length === 0)) {
    return (
      <Box sx={{ height: "200px" }}>
        <ErrorTemplate
          title={TEXT.DISCOVER_CAMERA_ERROR}
          description={TEXT.DISCOVER_CAMERA_DESC}
          testId="error_discovered_cameras"
          onRefresh={() => handleUnpairedCameraAPICall()}
        />;
      </Box>
    );
  }

  return (
    <>
      {showCameraOnboardingModal && (
        <DiscoverCamera
          data-testid="discoveredCameras_discoverCameraModal"
          onClose={() => {
            setShowCameraOnboardingModal(false);
            dispatch(modalOpen({}));
            dispatch(clearOnboardingFields(["selectedCamQR", "companionDevice"]));
          }}
          selectedSiteId={selectedSiteId}
        />
      )}
      <div style={{ maxWidth: "100%" }}>
        <div style={{ padding: "0 0 15px 0" }}>
          <DropdownSelect
            inlineLabel={true}
            label="Viewing:"
            error={false}
            width="290px"
            value={selectedSiteId}
            data-testid="discoveredCameras_siteViewingDropdownSelect"
            onChange={(e) => {
              setShowOfflineCCDsNotification(false);
              setShowOfflineCCDsNotificationMessage("");
              handleSiteToggle(e.target.value);
            }}
          >
            {filteredSites?.length > 0 && Array.isArray(filteredSites) ? (
              filteredSites.map((site, index) => (
                <option key={index} value={site.id}>{site?.title || site?.name}</option>
              ))
            ) : (
              <option value="">No Sites Available</option>
            )}
          </DropdownSelect>
        </div>

        {unpairedCameraDataSource?.length > 0 && (
          <Body size="large" bold>
            To pair a discovered device, click the camera ID.
          </Body>
        )}

        {showNotification && (
          <div
            style={{
              position: "fixed",
              top: 0,
              zIndex: 1050,
            }}
          >
            <NotificationBanner
              notificationInfo={{
                type: "No Permission",
                priority: "critical",
              }}
              setShowNotification={setShowNotification}
            />
          </div>
        )}

        {UnpairedCameraTableElement}
      </div>
    </>
  );
};

export default NewUnpairedCameraTab;
